import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import SignaturePad from '../../components/SignaturePad';
import { ArrowLeft, Camera, MapPin, Loader2, X, Eye, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useHideAgentFab } from '../../components/AgentFab';
import FormJobPicker, { type FormJobOption } from './FormJobPicker';
import { getUserDisplayName } from '../../lib/user-display';
import { downloadSubmissionPdf, submissionPdfFilename } from '../../lib/submission-pdf';
import SearchableSelect, { type SearchableOption } from '../../components/ui/SearchableSelect';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { todayCalendarDate } from '../../lib/calendar-date';
import { getRoleLabel } from '../../lib/roles';
import { applyPrefill, fieldPrefill, jobSupervisor, type PrefillContext } from '../../lib/safety-form-prefill';
import {
  indexFields,
  newFieldId,
  isFieldVisible as isVisibleGiven,
  normalizeChoices,
  visibleAnswers,
} from '../../lib/safety-form-logic';

/**
 * Radio and checkbox options: large targets (gloves), an optional grey
 * description line, and a wrapping row when the template asks for `inline`.
 */
function ChoiceOptions({
  field,
  value,
  onToggle,
}: {
  field: any;
  value: unknown;
  onToggle: (choice: string) => void;
}) {
  const radio = field.field_type === 'radio';
  const inline = field.options?.layout === 'inline';
  const picked = (v: string) => (radio ? value === v : Array.isArray(value) && value.includes(v));
  return (
    <div
      role={radio ? 'radiogroup' : 'group'}
      aria-label={field.label}
      className={inline ? 'flex flex-wrap gap-2' : 'space-y-2'}
    >
      {normalizeChoices(field.options).map(choice => {
        const selected = picked(choice.value);
        return (
          <label
            key={choice.value}
            className={`flex items-start gap-3 min-h-[48px] bg-fieldiq-card border rounded-lg px-3 py-3 cursor-pointer hover:border-fieldiq-accent/50 transition-colors ${
              inline ? 'flex-1 basis-[9rem] min-w-[9rem]' : ''
            } ${selected ? 'border-fieldiq-accent' : 'border-fieldiq-border'}`}
          >
            <input
              type={radio ? 'radio' : 'checkbox'}
              name={radio ? `field-${field.id}` : undefined}
              checked={selected}
              onChange={() => onToggle(choice.value)}
              className={`w-5 h-5 mt-0.5 shrink-0 ${radio ? '' : 'rounded'}`}
            />
            <span className="min-w-0">
              <span className="block text-sm text-white">{choice.value}</span>
              {choice.description && (
                <span className="block text-xs text-fieldiq-muted mt-0.5">{choice.description}</span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/** The job picker's key among the missing-field keys (field ids are UUIDs, so it cannot collide). */
const JOB_KEY = '__job';

/**
 * The red frame on a missing choice, signature or photo field — the ones with
 * no single input to put a red border on. Gloves on a phone: the field has to
 * be findable at a glance, so the whole answer area is outlined.
 */
/**
 * A dropdown's choices, plus its current value when the list does not name
 * it — a rig filled from the job that the template's list never had. Shown
 * as selected rather than leaving the field blank over a value it holds.
 */
function dropdownOptions(field: any, value: unknown): SearchableOption[] {
  const opts: SearchableOption[] = normalizeChoices(field.options).map(c => ({ value: c.value, label: c.value }));
  if (typeof value === 'string' && value && !opts.some(o => o.value === value)) opts.push({ value, label: value });
  return opts;
}

const missingFrame = (error: string | undefined) =>
  `rounded-lg border-2 ${error ? 'border-red-500 p-1.5' : 'border-transparent'}`;

export default function DynamicSafetyFormScreen() {
  useHideAgentFab();  // bottom action bar — the FAB would cover Submit.
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  /*
   * `?preview=1` is the template library's eye link. The form renders and
   * behaves exactly as a crew meets it, rules included, but nothing it holds
   * can be posted: Save Draft is disabled, and Submit runs the required-field
   * check and then stops before any request.
   */
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === '1';
  /*
   * `?draft=<submission id>` reopens one of the hand's own drafts ("My
   * drafts" on the Forms tab). Saving or submitting it goes through
   * PUT /safety/submissions/:id, which only the owner can do and only while
   * the row is still a draft.
   */
  const draftId = searchParams.get('draft');
  const queryClient = useQueryClient();

  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<Record<string, File[]>>({});
  const [photoPreviews, setPhotoPreviews] = useState<Record<string, string[]>>({});
  const [job, setJob] = useState<FormJobOption | null>(null);
  const [jobError, setJobError] = useState<string | undefined>();
  /*
   * The submission's id, minted once per form-open and sent as body `id` on
   * the POST. The API treats it as the primary key: a replay by the same user
   * returns the existing row (200) instead of a second one. So a Submit that
   * failed halfway (the row saved, a photo did not) can be pressed again
   * without filing a duplicate.
   */
  const [clientId] = useState(() => draftId ?? newFieldId());
  /** Set once a row exists on the server. After that every save is a PUT, because a POST replay changes nothing. */
  const [savedId, setSavedId] = useState<string | null>(draftId);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  // What has already reached the server, so a retry after a partial failure
  // sends only what is missing.
  const uploadedPhotos = useRef(new WeakSet<File>());
  const uploadedSignatures = useRef(new Map<string, string>());

  const { data: template, isLoading } = useQuery({
    queryKey: ['safety-template', templateId],
    queryFn: () => api.safety.getTemplate(templateId!).then(r => r.data?.data ?? r.data),
    enabled: !!templateId,
  });

  const { data: draft, isLoading: draftLoading } = useQuery({
    queryKey: ['safety-submission', draftId],
    queryFn: () => api.safety.getSubmission(draftId!).then(r => r.data?.data ?? r.data),
    enabled: !!draftId,
  });
  const prefilled = useRef(false);
  // Bumped once the draft's answers are in state. SignaturePad reads `value`
  // only when it mounts, and it mounts before this prefill lands, so a
  // reopened draft showed an empty pad over a signature it already had.
  // Keying the pad on this remounts it with the saved value.
  const [prefillKey, setPrefillKey] = useState(0);
  useEffect(() => {
    if (!draft || prefilled.current) return;
    prefilled.current = true;
    setAnswers(draft.answers || {});
    setPrefillKey(k => k + 1);
    if (draft.job_id) {
      setJob({
        id: draft.job_id,
        job_number: draft.job_number ?? null,
        status: null,
        scheduled_date: null,
        finished_at: null,
        customer_name: draft.customer_name ?? null,
        well_name: draft.well_name ?? null,
        well_id_lsd: null,
        location_city: null,
        location_province_state: null,
        rig_name: null,
      });
    }
    // A signature already on the server is not sent again.
    for (const sig of draft.signatures || []) {
      if (sig.field_id && draft.answers?.[sig.field_id]) {
        uploadedSignatures.current.set(sig.field_id, draft.answers[sig.field_id]);
      }
    }
  }, [draft]);
  const draftPhotos: any[] = draft?.photos || [];

  // Fetch workers for worker_select fields
  const hasWorkerSelect = (template?.fields || []).some((f: any) => f.field_type === 'worker_select');
  const { data: workers = [] } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => api.users.list().then(r => r.data?.data ?? r.data ?? []),
    enabled: hasWorkerSelect,
  });

  // Auto-capture GPS on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  /** Fields a person has edited. The prefill never writes one again, even after it is cleared. */
  const touched = useRef(new Set<string>());
  const setAnswer = (fieldId: string, value: any) => {
    touched.current.add(fieldId);
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) setErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  const fields: any[] = template?.fields || [];

  /*
   * Prefill ("Fill from" in the builder, options.prefill). Observer and Date
   * fill on open; the job-sourced fields fill when a job is linked, change
   * when it changes, and clear when it is removed. lib/safety-form-prefill
   * holds the rules: a hand-edited field or a draft's saved value is never
   * written over, and unlinking clears only what the job put there.
   */
  const auth = useOptionalAuth();
  const timeZone = useTenantTimeZone();
  const wantsSupervisor = fields.some(f => fieldPrefill(f) === 'job_supervisor');
  // Jobs have no supervisor column; the crew rows on GET /jobs/:id are where
  // one can be named (see jobSupervisor). A refused read leaves it blank.
  const { data: jobCrew } = useQuery({
    queryKey: ['safety-form-job-crew', job?.id],
    queryFn: () => api.jobs.get(job!.id).then(r => {
      const body = r.data;
      const row = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return row?.crew ?? null;
    }),
    enabled: !!job && wantsSupervisor,
    retry: false,
  });
  const authUser = auth?.user;
  const prefillCtx = useMemo<PrefillContext>(() => ({
    job,
    supervisor: job ? jobSupervisor(jobCrew) : null,
    user: authUser ? { id: authUser.id, name: getUserDisplayName(authUser as any, '') } : null,
    today: todayCalendarDate(timeZone),
  }), [job, jobCrew, authUser, timeZone]);
  const filled = useRef<Record<string, unknown>>({});
  // A reopened draft fills from its saved answers first (prefillKey bumps
  // once they are in state); prefilling before that would be written over.
  const ready = !!template && (!draftId || prefillKey > 0);
  // Read through a ref, not a setAnswers updater: the pass also records what
  // it filled, and an updater that writes a ref is not pure. StrictMode calls
  // updaters twice, the second call saw the first one's record, and on
  // "Remove job" it concluded the job's values were someone else's and kept
  // them. Only the dev build double-calls, so jsdom never showed it.
  const answersNow = useRef(answers);
  answersNow.current = answers;
  useEffect(() => {
    if (!ready) return;
    const next = applyPrefill(template.fields || [], answersNow.current, filled.current, touched.current, prefillCtx);
    filled.current = next.filled;
    if (next.answers !== answersNow.current) setAnswers(next.answers);
  }, [ready, template, prefillCtx]);

  /*
   * People for the worker pickers, with each prefilled person kept even when
   * the loaded list does not carry them, so a filled picker never reads as
   * empty.
   */
  const workerOptions = useMemo<SearchableOption[]>(() => {
    const opts: SearchableOption[] = (workers as any[]).map((w: any) => ({
      value: w.id,
      // A person's name; the email only when there is no name at all.
      label: getUserDisplayName(w, 'Unnamed user'),
      detail: w.role ? getRoleLabel(w.role) : null,
    }));
    for (const p of [prefillCtx.user, prefillCtx.supervisor]) {
      if (p && !opts.some(o => o.value === p.id)) opts.push({ value: p.id, label: p.name || 'Unnamed user' });
    }
    return opts;
  }, [workers, prefillCtx.user, prefillCtx.supervisor]);
  // The API refuses a submit without a job on these templates (400 since
  // api#1103) and never refuses a draft, so the check gates Submit only.
  const requiresJob = template?.requires_job_link === true;

  // Conditional logic: a field shows when its show_if rule is met (equals or
  // any_of), and never while its parent is itself hidden.
  const fieldsById = indexFields(fields);
  const isFieldVisible = (field: any) => isVisibleGiven(field, answers, fieldsById);

  /*
   * The first missing field after a refused Submit. Scrolling and focusing
   * wait for the render that draws the red frames, so this is state read by
   * an effect rather than a DOM call inside validate. `n` makes a second
   * Submit on the same field scroll again.
   */
  const [firstMissing, setFirstMissing] = useState<{ key: string; n: number } | null>(null);
  useEffect(() => {
    if (!firstMissing) return;
    const el = document.querySelector<HTMLElement>(`[data-field-key="${firstMissing.key}"]`);
    if (!el) return;
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    // A typed or picked control takes focus; a signature or photo has none
    // worth landing on, so the frame itself does.
    const control = el.querySelector<HTMLElement>('input:not([type="file"]), select, textarea');
    const signatureOrPhoto = el.dataset.focusFrame === 'true';
    (signatureOrPhoto || !control ? el : control).focus({ preventScroll: true });
  }, [firstMissing]);

  const hasPhoto = (fieldId: string) =>
    (photoFiles[fieldId]?.length ?? 0) > 0 || draftPhotos.some(p => p.field_id === fieldId);

  /** Returns the keys of every missing required field, in page order, job first. Hidden fields never count. */
  const validate = () => {
    const newErrors: Record<string, string> = {};
    fields.forEach((f: any) => {
      if (!isFieldVisible(f) || !f.is_required) return;
      if (['section_header', 'instructions', 'gps_location'].includes(f.field_type)) return;
      if (f.field_type === 'photo') {
        if (!hasPhoto(f.id)) newErrors[f.id] = 'Required';
        return;
      }
      const val = answers[f.id];
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
        newErrors[f.id] = 'Required';
      }
    });
    setErrors(newErrors);
    const jobMissing = requiresJob && !job;
    setJobError(jobMissing ? 'Pick the job this form is for' : undefined);
    return [...(jobMissing ? [JOB_KEY] : []), ...fields.filter((f: any) => newErrors[f.id]).map((f: any) => f.id)];
  };

  const handleSubmit = async (asDraft: boolean) => {
    // A draft is unfinished by definition, so it is never checked. Preview
    // runs the check (so a template author can see what a crew will be told)
    // and then stops: nothing it holds is posted.
    if (!asDraft) {
      const missing = validate();
      if (missing.length > 0) {
        toast.error(`${missing.length} required field${missing.length === 1 ? '' : 's'} missing`);
        setFirstMissing(prev => ({ key: missing[0], n: (prev?.n ?? 0) + 1 }));
        return;
      }
    }
    if (preview) {
      if (!asDraft) toast.success('Every required field is filled. Preview — nothing was sent.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        // A hidden field's answer is not sent. Picking a radio option, filling
        // its child, then changing the radio would otherwise file the child's
        // answer on a form where that question was never asked.
        answers: visibleAnswers(fields, answers),
        // The API reads these two columns. `gps_location: {lat, lng}` (what
        // this sent before) was never read, so no submission kept a location.
        ...(gpsCoords ? { gps_latitude: gpsCoords.lat, gps_longitude: gpsCoords.lng } : {}),
        ...(job ? { job_id: job.id } : {}),
      };
      let submissionId = savedId;
      if (submissionId) {
        await api.safety.updateSubmission(submissionId, asDraft ? body : { ...body, status: 'submitted' });
      } else {
        const res = await api.safety.createSubmission({
          id: clientId,
          template_id: templateId,
          status: asDraft ? 'draft' : 'submitted',
          ...body,
        });
        submissionId = res.data?.data?.id || res.data?.id || clientId;
        setSavedId(submissionId);
      }

      // Photos and signatures need the row to exist. They go up on a draft
      // save too, so a reopened draft still has them.
      for (const [fieldId, files] of Object.entries(photoFiles)) {
        const photoField = fieldsById.get(fieldId);
        if (photoField && !isFieldVisible(photoField)) continue;
        for (const file of files) {
          if (uploadedPhotos.current.has(file)) continue;
          const formData = new FormData();
          // `file` is the key the route reads. This sent `photo`, which 400'd
          // after the row already existed, so no form photo ever saved.
          // No Content-Type here: the browser sets the multipart boundary.
          formData.append('file', file);
          formData.append('field_id', fieldId);
          await api.safety.uploadSubmissionPhotos(submissionId!, formData);
          uploadedPhotos.current.add(file);
        }
      }
      for (const f of fields) {
        const data = answers[f.id];
        if (f.field_type !== 'signature' || !data || !isFieldVisible(f)) continue;
        if (uploadedSignatures.current.get(f.id) === data) continue;
        // `image` is the key the route reads (this sent `signature_data`).
        await api.safety.uploadSubmissionSignature(submissionId!, {
          image: data,
          field_id: f.id,
          name: f.label || 'Signature',
        });
        uploadedSignatures.current.set(f.id, data);
      }

      queryClient.invalidateQueries({ queryKey: ['safety-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['safety-drafts'] });
      // The Records Custom Forms list, which an office user returns to.
      queryClient.invalidateQueries({ queryKey: ['safety-records-custom-forms'] });
      if (draftId) queryClient.invalidateQueries({ queryKey: ['safety-submission', draftId] });
      if (asDraft) {
        toast.success('Draft saved');
        navigate(-1);
      } else {
        toast.success('Form submitted successfully');
        setSubmittedId(submissionId);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoCapture = (fieldId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (errors[fieldId]) setErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
    setPhotoFiles(prev => ({ ...prev, [fieldId]: [...(prev[fieldId] || []), ...files] }));
    const previews = files.map(f => URL.createObjectURL(f));
    setPhotoPreviews(prev => ({ ...prev, [fieldId]: [...(prev[fieldId] || []), ...previews] }));
  };

  const removePhoto = (fieldId: string, index: number) => {
    setPhotoFiles(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
    }));
    setPhotoPreviews(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
    }));
  };

  if (isLoading || (draftId && draftLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
        <p className="text-white text-lg font-semibold mb-2">Form not found</p>
        <button onClick={() => navigate(-1)} className="text-fieldiq-accent text-sm">Go back</button>
      </div>
    );
  }

  if (submittedId) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
        <CheckCircle size={40} className="text-fieldiq-success mb-3" aria-hidden />
        <p className="text-white text-lg font-semibold mb-1">Form submitted</p>
        <p className="text-sm text-fieldiq-text mb-6">{template.name}</p>
        <div className="flex w-full max-w-sm flex-col gap-3">
          <button
            type="button"
            onClick={() => downloadSubmissionPdf(submittedId, submissionPdfFilename(template.name, submittedId))}
            className="flex items-center justify-center gap-2 py-3 bg-fieldiq-card border border-fieldiq-border text-white font-semibold rounded-xl hover:bg-fieldiq-border transition-colors"
          >
            <Download size={18} aria-hidden /> Download PDF
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="py-3 bg-fieldiq-accent text-white font-semibold rounded-xl hover:bg-fieldiq-accent/90 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white hover:text-amber-400 text-base font-semibold px-1 py-1 rounded transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <h1 className="text-base font-bold text-foreground flex-1 truncate">
          {template.name}
          {savedId && !preview && <span className="ml-2 text-xs font-medium text-fieldiq-muted">Draft</span>}
        </h1>
        {submitting && <Loader2 size={18} className="animate-spin text-fieldiq-accent" />}
      </div>

      {preview && (
        <div
          role="status"
          className="flex items-center justify-center gap-2 border-b border-fieldiq-border bg-fieldiq-accent/15 px-4 py-2 text-sm font-semibold text-fieldiq-text"
        >
          <Eye size={16} aria-hidden /> Preview — not saved
        </div>
      )}

      {/* Form fields */}
      <div className="px-4 py-4 space-y-4 max-w-lg mx-auto">
        {template.description && (
          <p className="text-fieldiq-text text-sm">{template.description}</p>
        )}

        <div data-field-key={JOB_KEY} tabIndex={jobError ? -1 : undefined} className="outline-none scroll-mt-4">
          <FormJobPicker
            value={job}
            onChange={j => { setJob(j); if (j) setJobError(undefined); }}
            required={requiresJob}
            error={jobError}
          />
        </div>

        {fields.map((field: any) => {
          if (!isFieldVisible(field)) return null;
          const error = errors[field.id];

          return (
            <div
              key={field.id}
              className="space-y-1 outline-none scroll-mt-4"
              data-field-key={field.id}
              data-invalid={error ? 'true' : undefined}
              data-focus-frame={field.field_type === 'signature' || field.field_type === 'photo' ? 'true' : undefined}
              tabIndex={error ? -1 : undefined}
            >
              {/* Section header */}
              {field.field_type === 'section_header' && (
                <div className="border-b border-fieldiq-border pb-1 pt-2">
                  <p className="text-sm font-bold text-white">{field.label}</p>
                </div>
              )}

              {/* Instructions */}
              {field.field_type === 'instructions' && (
                <p className="text-sm text-fieldiq-muted italic">{field.label}</p>
              )}

              {/* Text input */}
              {field.field_type === 'text' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <input
                    type="text"
                    value={answers[field.id] || ''}
                    onChange={e => setAnswer(field.id, e.target.value)}
                    placeholder={field.placeholder || ''}
                    className={`w-full bg-fieldiq-card border rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-fieldiq-accent ${
                      error ? 'border-red-500' : 'border-fieldiq-border'
                    }`}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Textarea */}
              {field.field_type === 'textarea' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <textarea
                    value={answers[field.id] || ''}
                    onChange={e => setAnswer(field.id, e.target.value)}
                    placeholder={field.placeholder || ''}
                    rows={3}
                    className={`w-full bg-fieldiq-card border rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-fieldiq-accent resize-none ${
                      error ? 'border-red-500' : 'border-fieldiq-border'
                    }`}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Number */}
              {field.field_type === 'number' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <input
                    type="number"
                    value={answers[field.id] ?? ''}
                    onChange={e => setAnswer(field.id, e.target.value)}
                    placeholder={field.placeholder || ''}
                    className={`w-full bg-fieldiq-card border rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-fieldiq-accent ${
                      error ? 'border-red-500' : 'border-fieldiq-border'
                    }`}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Yes/No toggle */}
              {field.field_type === 'yes_no' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <div className={`flex gap-2 ${missingFrame(error)}`}>
                    {['Yes', 'No'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setAnswer(field.id, opt)}
                        className={`flex-1 py-3 rounded-lg text-sm font-medium transition-colors ${
                          answers[field.id] === opt
                            ? opt === 'Yes' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                            : 'bg-fieldiq-card border border-fieldiq-border text-fieldiq-text'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Checkbox group */}
              {field.field_type === 'checkbox_group' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <div className={missingFrame(error)}>
                  <ChoiceOptions
                    field={field}
                    value={answers[field.id]}
                    onToggle={choice => {
                      const current: string[] = answers[field.id] || [];
                      setAnswer(field.id, current.includes(choice) ? current.filter(c => c !== choice) : [...current, choice]);
                    }}
                  />
                  </div>
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Radio — one value */}
              {field.field_type === 'radio' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <div className={missingFrame(error)}>
                  <ChoiceOptions
                    field={field}
                    value={answers[field.id]}
                    onToggle={choice => setAnswer(field.id, choice)}
                  />
                  </div>
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Dropdown */}
              {field.field_type === 'dropdown' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <SearchableSelect
                    label={field.label}
                    options={dropdownOptions(field, answers[field.id])}
                    value={answers[field.id] || ''}
                    onChange={v => setAnswer(field.id, v)}
                    invalid={!!error}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Date */}
              {field.field_type === 'date' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  <input
                    type="date"
                    value={answers[field.id] || ''}
                    onChange={e => setAnswer(field.id, e.target.value)}
                    className={`w-full bg-fieldiq-card border rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-fieldiq-accent ${
                      error ? 'border-red-500' : 'border-fieldiq-border'
                    }`}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Time */}
              {field.field_type === 'time' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  <input
                    type="time"
                    value={answers[field.id] || ''}
                    onChange={e => setAnswer(field.id, e.target.value)}
                    className={`w-full bg-fieldiq-card border rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-fieldiq-accent ${
                      error ? 'border-red-500' : 'border-fieldiq-border'
                    }`}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Photo capture */}
              {field.field_type === 'photo' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  <div className={missingFrame(error)}>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {/* Already on the server (a reopened draft). The url is a
                        fresh presigned one from this read; it is not kept. */}
                    {draftPhotos.filter(p => p.field_id === field.id && p.url).map((p, idx) => (
                      <img key={`saved-${idx}`} src={p.url} alt="" className="w-20 h-20 object-cover rounded-lg" />
                    ))}
                    {(photoPreviews[field.id] || []).map((preview, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img src={preview} alt="" className="w-full h-full object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removePhoto(field.id, idx)}
                          className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"
                        >
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center justify-center gap-2 bg-fieldiq-card border-2 border-dashed border-fieldiq-border rounded-lg px-4 py-4 cursor-pointer hover:border-fieldiq-accent transition-colors">
                    <Camera size={20} className="text-fieldiq-muted" />
                    <span className="text-sm text-fieldiq-muted">Tap to capture photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={e => handlePhotoCapture(field.id, e)}
                      className="hidden"
                    />
                  </label>
                  </div>
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* Signature */}
              {field.field_type === 'signature' && (
                <div>
                  <div className={`rounded-xl border-2 ${error ? 'border-red-500' : 'border-transparent'}`}>
                    <SignaturePad
                      key={`${field.id}:${prefillKey}`}
                      label={`${field.label || 'Signature'}${field.is_required ? ' *' : ''}`}
                      value={answers[field.id]}
                      onSave={dataUrl => setAnswer(field.id, dataUrl)}
                      onClear={() => setAnswer(field.id, '')}
                    />
                  </div>
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}

              {/* GPS Location */}
              {field.field_type === 'gps_location' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">{field.label}</label>
                  <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-fieldiq-text">
                      <MapPin size={16} className="text-fieldiq-accent" />
                      {gpsCoords ? (
                        <span>{gpsCoords.lat.toFixed(6)}, {gpsCoords.lng.toFixed(6)}</span>
                      ) : (
                        <span className="text-fieldiq-muted">Acquiring location...</span>
                      )}
                    </div>
                    <input
                      type="text"
                      value={answers[field.id] || ''}
                      onChange={e => setAnswer(field.id, e.target.value)}
                      placeholder="Location name (optional)"
                      className="w-full bg-fieldiq-dark border border-fieldiq-border rounded px-2.5 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent"
                    />
                  </div>
                </div>
              )}

              {/* Worker select */}
              {field.field_type === 'worker_select' && (
                <div>
                  <label className="text-sm text-fieldiq-text mb-1 block">
                    {field.label}{field.is_required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.help_text && <p className="text-xs text-fieldiq-muted mb-1">{field.help_text}</p>}
                  <SearchableSelect
                    label={field.label}
                    options={workerOptions}
                    multiple={field.options?.multiple === true}
                    // Always string[] on the wire. Single (the default) just
                    // means picking one replaces the other.
                    value={Array.isArray(answers[field.id]) ? answers[field.id] : answers[field.id] ? [answers[field.id]] : []}
                    onChange={v => setAnswer(field.id, Array.isArray(v) ? v : v ? [v] : [])}
                    placeholder={field.options?.multiple ? 'Select people...' : 'Select a person...'}
                    invalid={!!error}
                  />
                  {error && <p className="text-xs text-red-400 mt-0.5">{error}</p>}
                </div>
              )}
            </div>
          );
        })}

        {/* Submit buttons */}
        <div className="flex gap-3 pt-4 pb-[var(--bottom-chrome)]">
          <button
            type="button"
            onClick={() => handleSubmit(true)}
            disabled={submitting || preview}
            className="flex-1 py-3 bg-fieldiq-card border border-fieldiq-border text-white font-semibold rounded-xl hover:bg-fieldiq-border transition-colors disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="flex-1 py-3 bg-fieldiq-accent text-white font-semibold rounded-xl hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
